import { DateTime } from "luxon"
import { VersionedData } from "@webapp/util/AsyncData"
import { CompositeId } from "./CompositeId"
import { isDefined } from "@webapp/util/TypeScriptUtil"
import { HistoricalDate } from "./HistoricalDate"
import { SortDate } from "./SortDate"
import { CountryCodes, GeoUtil } from '@webapp/util/GeoUtil'
import { DurationUtil } from "@webapp/util/LuxonUtil"
import { FuzzyDate } from "./FuzzyDate"

export class Group {
    id?: string
    profile = false
    primaryPersonId?: string
    modifiedDate?: DateTime
    dataModifiedDate?: DateTime
    processingCheckpoint?: DateTime

    get status() { return this.dataModifiedDate && this.processingCheckpoint && this.processingCheckpoint >= this.dataModifiedDate ? GroupStatus.Completed : GroupStatus.Incomplete }
    get statusText() { return this.status.toString() }
}

export enum GroupStatus {
    Incomplete = "Incomplete",
    Completed = "Completed",
}

export interface PersonLike {
    id?: string
    displayProperties: PersonDisplayProperties
    pageUrl?: string
}

export class Person implements PersonLike, VersionedData {
    id?: string
    modifiedUserId?: string
    modifiedDate?: DateTime
    userRestricted = false
    autoRestricted = false
    profile = false
    placeholder = false
    displayProperties = new PersonDisplayProperties()
    familyRelationships?: FamilyRelationship[]

    get groupId() { return CompositeId.getGroupId(this.id)}
    get displayName() { return this.displayProperties.displayName }
    get pageUrl() { return Person.getPageUrl(this.id) }
    get viewPersonUrl() { return `/persons/${this.id}` }
    get restricted() { return this.userRestricted || this.autoRestricted }
    get childRelationships() { return this.familyRelationships?.filter(r => r.role == FamilyRelationshipRole.Child) ?? [] }
    get spouseRelationships() { return this.familyRelationships?.filter(r => r.role == FamilyRelationshipRole.Father || r.role == FamilyRelationshipRole.Mother) ?? []}

    hasGroupId(groupId: string) { return CompositeId.hasGroupId(this.id, groupId) }

    isChildInFamily(familyId: string) {
        return this.childRelationships.some(r => r.familyId == familyId)
    }

    static compareDisplayName(a: Person, b: Person) {
        return (a.displayName || '').localeCompare(b.displayName || '')
    }

    static getPageUrl(personId: string | undefined) { return `/profiles/${personId}` }
    static getMatchesPageUrl(personId: string | undefined) { return `/profiles/${personId}/matches` }
}

export class PersonDisplayProperties {
    name?: string
    gender?: Gender
    lifespan?: string
    birthDate = HistoricalDate.Empty
    birthSortDate = new SortDate()
    birthPlaceId?: string
    deathDate = HistoricalDate.Empty
    deathSortDate = new SortDate()
    deathPlaceId?: string
    living = false
    primaryPhotoSignedUrl?: ContentDataSignedUrl

    get displayName() { return PersonName.getDisplayName(this.name) }
    get birthYear() { return this.birthDate.date1?.year }
    get deathYear() { return this.deathDate.date1?.year }
    get assumeDeceased() { 
        return !!this.deathYear || (!!this.birthYear && this.birthYear < livingWindowStart) // ignore living assertion
    }
    get recentlyLiving() {
        return !this.birthYear || this.birthYear >= (livingWindowStart - 50)
    }

    get lifespanText() {
        const birthDate = this.birthSortDate.date1
        const deathDate = this.deathSortDate.date1
      
        if (birthDate && !birthDate.isEmpty && deathDate && !deathDate.isEmpty) {
          const age = deathDate.diff(birthDate)
          return `${birthDate.year}-${deathDate.year} (${DurationUtil.toAge(age, true)})`
        }
      
        if (this.living) {
          if (birthDate && !birthDate.isEmpty) {
            const age = FuzzyDate.today().diff(birthDate)
            return `${birthDate.year}- (age ${age.years})`
          }
          return "Living"
        }
      
        return this.lifespan
    }

    static isRelatedAssertionType(assertionType: string | undefined) {
        switch (assertionType) {
            case AssertionType.Name:
            case AssertionType.Gender:
            case AssertionType.Birth:
            case AssertionType.Christen:
            case AssertionType.Death:
            case AssertionType.Burial:
            case AssertionType.Cremation:
            case AssertionType.Living:
                return true
        }
        return false
    }
}

export enum PersonNamePart {
    Prefix = "pf",
    Given = "g",
    Surname = "s",
    Suffix = "sf",
}

export function getPersonNamePartDisplayText(part: PersonNamePart) {
    switch (part) {
        case PersonNamePart.Prefix: return "prefix"
        case PersonNamePart.Given: return "given name"
        case PersonNamePart.Surname: return "surname"
        case PersonNamePart.Suffix: return "suffix"
    }
}

export class PersonName {
    prefix?: string
    given?: string
    surname?: string
    suffix?: string

    constructor(nameXml?: string) {
        // use replace because you can get capturing groups from all matches
        nameXml?.replace(/<(\w+)>([^<]*)<\/\w+>/g, (match, elem, value) => {
            switch (elem) {
                case PersonNamePart.Prefix: this.prefix = value; break
                case PersonNamePart.Given: this.given = value; break
                case PersonNamePart.Surname: this.surname = value; break
                case PersonNamePart.Suffix: this.suffix = value; break
                default:
                    throw 'PersonName.UnknownNamePart'
            }
            return match // no-op -- we don't care about the result
        })
    }

    get givenNames() { return this.given?.split(' ') ?? [] }
    get surnames() { return this.surname?.split(' ') ?? [] }

    getPart(part: PersonNamePart) {
        switch (part) {
            case PersonNamePart.Prefix: return this.prefix
            case PersonNamePart.Given: return this.given
            case PersonNamePart.Surname: return this.surname
            case PersonNamePart.Suffix: return this.suffix
        }
    }

    getValue() {
        // TODO: Allow user to customize display format and persist it
        const parts: string[] = []
        if (this.prefix) parts.push(`<pf>${this.prefix}</pf>`)
        if (this.given) parts.push(`<g>${this.given}</g>`)
        if (this.surname) parts.push(`<s>${this.surname}</s>`)
        if (this.suffix) parts.push(`<sf>${this.suffix}</sf>`)
        return parts.join(' ')
    }

    static getDisplayName(nameXml: string | undefined) {
        return nameXml
            ?.replace('<pf>', '').replace('</pf>', '')
            .replace('<g>', '').replace('</g>', '')
            .replace('<s>', '').replace('</s>', '')
            .replace('<sf>', '').replace('</sf>', '');
    }
   
}

export interface NewFamily {
    fatherId?: string
    motherId?: string
}

export interface FamilyLike {
    fatherId?: string
    motherId?: string
}

export class Family implements VersionedData, FamilyLike {
    id?: string
    fatherId?: string
    motherId?: string
    modifiedUserId?: string
    modifiedDate?: DateTime
    restricted = false
    profile = false
    displayProperties = new FamilyDisplayProperties()

    get groupId() { return CompositeId.getGroupId(this.id)}
    get spouseIds() { return [this.fatherId, this.motherId].filter(isDefined) }
    get isComplete() { return isCompleteFamily(this) }

    hasGroupId(groupId: string) { return CompositeId.hasGroupId(this.id, groupId) }

    hasSpouse(spouseId: string) { return hasSpouse(this, spouseId) }

    getSpouseOf(personId: string) { return spouseOf(this, personId) }

    getSpouseId(role: FamilyRelationshipRole) { return getSpouseId(this, role) }
    
    roleOf(spouseId: string) { return roleOfSpouse(this, spouseId) }

    static getDisplayName(fatherDisplayName: string | undefined, motherDisplayName: string | undefined) {
        return `${fatherDisplayName || "(unknown)"} and ${motherDisplayName || "(unknown)"}`
    }
}

export class FamilyDisplayProperties {
    marriageDate = HistoricalDate.Empty
    marriageSortDate = new SortDate()
    marriagePlaceId?: string
}

export function isCompleteFamily(family: FamilyLike) {
    return !!family.fatherId && !!family.motherId
}

function hasSpouse(family: FamilyLike, spouseId: string) {
    return family.fatherId == spouseId || family.motherId == spouseId    
}

function spouseOf(family: FamilyLike, personId: string) {
    if (family.fatherId == personId)
        return family.motherId
    if (family.motherId == personId)
        return family.fatherId
}

function getSpouseId(family: FamilyLike, role: FamilyRelationshipRole) {
    switch (role) {
        case FamilyRelationshipRole.Father: return family.fatherId
        case FamilyRelationshipRole.Mother: return family.motherId
    }
}

function roleOfSpouse(family: FamilyLike, spouseId: string) {
    if (family.fatherId == spouseId)
        return FamilyRelationshipRole.Father
    if (family.motherId == spouseId)
        return FamilyRelationshipRole.Mother
}

export interface NewAssertion {
    subjectId?: string
    subjectType?: ItemType
    assertionType?: string
    relatedItemId?: string
    relatedItemType?: ItemType
    value?: string
    date?: HistoricalDate
    sortDate?: SortDate
    placeId?: string
    siteId?: string
    restricted?: boolean
}

export class Assertion implements VersionedData {
    id?: string
    subjectId?: string
    subjectType = ItemType.Person
    assertionType = ''
    relatedItemId?: string
    relatedItemType?: ItemType
    value?: string
    date = HistoricalDate.Empty
    sortDate = SortDate.Empty
    placeId?: string
    siteId?: string
    modifiedUserId?: string
    modifiedDate?: DateTime
    restricted = false
    profile = false

    precedence?: AssertionPrecedence

    get groupId() { return CompositeId.getGroupId(this.id)}
    get sortValue() { return this.sortDate.isEmpty ? this.date.sortValue : this.sortDate.value }
    get defaultSortLast() { return Assertion.getDefaultSortLast(this.assertionType) }

    static comparePrecedence(a: Assertion, b: Assertion) {
        // missing precendence sorts FIRST (arbitrary convention)
        if (!a.precedence)
            return (b.precedence ? -1 : 0)
        if (!b.precedence)
            return 1
        return a.precedence.rank - b.precedence.rank
    }

    static getDefaultSortLast(type: string) {
        switch (type) {
            case AssertionType.Death:
            case AssertionType.Burial:
            case AssertionType.Cremation:
            case AssertionType.Cemetery:
                return true
        }
        return false
    }

    static compareDate(a: Assertion, b: Assertion) {
        // compute sort values once
        const aSortValue = a.sortValue
        const bSortValue = b.sortValue
        // sort missing dates FIRST (unless default sort last)
        if (!aSortValue) {
            return bSortValue ? (a.defaultSortLast ? 1 : -1) : 0 // a first (or last) or equal if both missing
        }
        if (!bSortValue) {
            return b.defaultSortLast ? -1 : 1 // b first (or last)
        }
        return aSortValue.localeCompare(bSortValue)
    }
}

export interface AssertionPrecedence {
    instanceId: number
    variationId: number
    rank: number
    confidence: ConfidenceLevel
    trustLevel: number
}

export enum ConfidenceLevel
{
    // NOTE: Numeric values ensure sorting behavior is explicitly defined
    High = 0,
    Medium = 1,
    Low = 2,
    Dismissed = 3,
}

export class AssertionTypeDefinition {
    type?: string
    name?: string
    useValue = false
    useDate = false
    useLocation = false
    subjectTypes: ItemType[] = []
    relatedItemTypes: ItemType[] = []
}

export class Place implements VersionedData {
    id?: string
    name?: string
    shortName?: string
    latitude?: number
    longitude?: number
    modifiedUserId?: string
    modifiedDate?: DateTime
    restricted = false
    profile = false

    get groupId() { return CompositeId.getGroupId(this.id)}
    get countryName() { return GeoUtil.getCountryName(this.name) }
    get countryCode() { return CountryCodes.fromName(this.countryName) }

    distanceTo(other: Place) {
        if (!this.latitude || !this.longitude || !other.latitude || !other.longitude) {
            return undefined
        }
        return GeoUtil.getDistance(this.latitude, this.longitude, other.latitude, other.longitude)
    }
}

export class Site implements VersionedData {
    id?: string
    description?: string
    placeId?: string
    latitude?: number
    longitude?: number
    modifiedUserId?: string
    modifiedDate?: DateTime
    restricted = false
    profile = false

    get groupId() { return CompositeId.getGroupId(this.id)}
}

export class Content implements VersionedData {
    id?: string
    fileName?: string
    caption?: string
    description?: string
    date = HistoricalDate.Empty
    refNumber?: string
    dataStatus = ContentDataStatus.NoData
    dataModifiedDate?: DateTime
    dataSize?: number
    dataHash?: string
    dataSignedUrls?: ContentDataSignedUrl[]
    profile = false
    modifiedUserId?: string
    modifiedDate?: DateTime
    createdDate?: DateTime
    restricted = false
    reactions?: { likeCount: number }

    get hasData() { return this.dataStatus == ContentDataStatus.Available && !!this.originalUrl }
    get thumbUrl() { return this.bestUrl(ContentDataAltKey.Thumb) }
    get galleryUrl() { return this.bestUrl(ContentDataAltKey.Gallery) }
    get largeUrl() { return this.bestUrl(ContentDataAltKey.Large) }
    get originalUrl() { return this.dataSignedUrls?.find(s => s.altKey == undefined)?.signedUrl }

    get originalFileExt() { 
        if (this.hasData) {
            const path = new URL(this.originalUrl!).pathname
            return path.slice(path.lastIndexOf('.') + 1).toLowerCase()
        }
    }
    get isImage() { return imageContentTypeInfo.extensions.includes(this.originalFileExt ?? '') }
    get icon() { return contentTypeInfos.find(i => i.extensions.includes(this.originalFileExt ?? ''))?.icon }

    bestUrl(altKey: ContentDataAltKey) {
        return this.dataSignedUrls?.find(s => s.altKey == altKey)?.signedUrl ?? this.originalUrl
    }   
}

export interface ContentTypeInfo {
    type: string
    extensions: string[]
    icon: any,
}

export const imageContentTypeInfo: ContentTypeInfo = {
    type: 'image',
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff'],
    icon: undefined,
}

export const contentTypeInfos: ContentTypeInfo[] = [
    imageContentTypeInfo,
    {
        type: 'pdf',
        extensions: ['pdf'],
        icon: new URL('@webapp/assets/logos/pdf.svg', import.meta.url).href,
    },
    {
        type: 'docx',
        extensions: ['docx', 'doc'],
        icon: new URL('@webapp/assets/logos/docx.svg', import.meta.url).href,
    },
    {
        type: 'txt',
        extensions: ['txt'],
        icon: new URL('@webapp/assets/logos/txt.svg', import.meta.url).href,
    },
]

export enum ContentDataStatus {
    NoData = "NoData",
    Processing = "Processing",
    Available = "Available",
    ProcessingError = "ProcessingError",
    Blocked = "Blocked",
}

export enum ContentDataAltKey {
    Thumb = "thumb",
    Gallery = "gallery",
    Large = "large",
}

export function getContentDataAltSize(altKey: ContentDataAltKey) {
    switch (altKey) {
        case ContentDataAltKey.Thumb: return 80
        case ContentDataAltKey.Gallery: return 320 // 4x thumb
        case ContentDataAltKey.Large: return 1920 // 6x gallery
    }
}

export interface ContentDataSignedUrl {
    contentId: string
    altKey?: ContentDataAltKey
    signedUrl: string
    expirationDate: DateTime
}

export class ContentTag implements VersionedData {
    id?: string
    itemType = ItemType.Person
    itemId?: string
    contentId?: string
    comment?: string
    isPrimary = false
    profile = false
    modifiedUserId?: string
    modifiedDate?: DateTime
    createdDate?: DateTime
    restricted = false
}

export class StandardPlace {
    id = ''
    fullName = ''
    levels: string[] = []
    latitude?: number
    longitude?: number

    get name() {
        return this.levels.filter(isDefined).join(GeoUtil.LevelDelimiter)
    }
}

export const livingWindowStart = DateTime.now().year - 110

export class PersonSet {
    id?: string
    memberIds: string[] = []
    modifiedUserId?: string
    modifiedDate?: DateTime

    get groupId() { return CompositeId.getGroupId(this.id) }
}

/**
 * A lightweight person loaded inline with a view person that is only used to distinguish person types. 
 * This helps avoid unnecessary loading of other resources (such as data groups or full persons) to get 
 * the same information.
 */
export class MatchPerson {
    id?: string
    userRestricted = false
    autoRestricted = false
    profile = false
    placeholder = false

    get groupId() { return CompositeId.getGroupId(this.id)}
    get restricted() { return this.userRestricted || this.autoRestricted }
}

export class ViewPerson implements PersonLike {
    id?: string
    matchIds: string[] = []
    displayProperties = new PersonDisplayProperties()

    get displayName() { return this.displayProperties.displayName ?? "(unknown name)" }
    get pageUrl() { return this.id ? ViewPerson.getPageUrl(this.id) : undefined }

    // TODO: Change references to use displayProperties directly
    get birthYear() { return this.displayProperties.birthYear }
    get deathYear() { return this.displayProperties.deathYear }
    get assumeDeceased() { return this.displayProperties.assumeDeceased }
    get recentlyLiving() { return this.displayProperties.recentlyLiving }

    get groupIds() { return this.matchIds.map(id => CompositeId.getGroupId(id)!) } // better be valid ids! :)

    static getPageUrl(personId: string) { return `/persons/${personId}` }
}

export interface FamilyRelationship {
    familyId: string
    role: FamilyRelationshipRole
    childOrder?: number
}

export enum FamilyRelationshipRole {
    Father = "Father",
    Mother = "Mother",
    Child = "Child",
}

export class ViewFamily implements FamilyLike {
    id?: string
    fatherId?: string
    motherId?: string
    matchIds: string[] = []
    displayProperties = new FamilyDisplayProperties()

    get pageUrl() { return `/families/${this.id}` }
    get spouseIds() { return [this.fatherId, this.motherId].filter(isDefined) }
    get isComplete() { return isCompleteFamily(this) }
    get groupIds() { return this.matchIds.map(id => CompositeId.getGroupId(id)!) } // better be valid ids! :)

    hasSpouse(spouseId: string) { return hasSpouse(this, spouseId) }

    spouseOf(personId: string) { return spouseOf(this, personId) }

    getSpouseId(role: FamilyRelationshipRole) { return getSpouseId(this, role) }
    
    roleOf(spouseId: string) { return roleOfSpouse(this, spouseId) }

    static getDisplayName(father: ViewPerson | undefined, mother: ViewPerson | undefined) {
        return Family.getDisplayName(father?.displayName, mother?.displayName)
    }
}

export interface ValueCount {
    value: string
    count: number
}

// The device that produced a sync -- a superset of syncapp's DeviceInfo, plus the web browser.
export interface ClientDeviceInfo {
    name?: string
    manufacturer?: string
    model?: string
    os?: string
    browser?: string
}

export class Workspace {
    id?: string
    viewGroupId?: string
    clientDeviceInfo?: string
    dataFileType?: string
    dataFilePath?: string
    readonly syncOptions = new SyncOptions()
    ownerId?: string
    noProfiles = false
    modifiedDate?: DateTime
    modifiedUserId?: string
    lastSyncId?: string

    get sourceFilePath() { return this.dataFilePath }
    get sourceFileType() { return this.dataFileType }
    get sourceFileBaseType() { return this.sourceFileType?.split('.')[0] ?? 'Unknown' }
    get sourceFileFullName() { return this.dataFilePath?.split('\\').pop() }
    get sourceFileName() { return this.sourceFileFullName?.slice(0, this.sourceFileFullName.lastIndexOf('.')) }
    get sourceFileExt() { return this.sourceFileFullName?.slice(this.sourceFileFullName.lastIndexOf('.')) }
    get sourceFileFolder() { return this.dataFilePath ? this.dataFilePath.slice(0, this.dataFilePath.lastIndexOf('\\')) : undefined }

    // DEPRECATED: Use the "source" properties instead
    get dataFileBaseType() { return this.sourceFileBaseType }
    get dataFileFullName() { return this.sourceFileFullName }
    get dataFileName() { return this.sourceFileName }
    get dataFileExt() { return this.sourceFileExt }
    get dataFileFolder() { return this.sourceFileFolder }

    get workspaceTypeInfo() { return workspaceTypeInfos.find(t => t.id == this.dataFileBaseType) }
    get clientDevice(): ClientDeviceInfo { return JSON.parse(this.clientDeviceInfo ?? '{}') }

    // A named device (the desktop app) reads as "name (model)". A browser upload has no device
    // identity, so it reads as its origin -- "Uploaded from Windows (Edge)" -- and drops the
    // browser unless it's a short, recognizable name.
    get clientDeviceLabel(): string | undefined {
        const { name, model, os, browser } = this.clientDevice
        if (name) {
            return model ? `${name} (${model})` : name
        }
        if (os) {
            const clearBrowser = browser && browser.length <= 20 ? browser : undefined
            return clearBrowser ? `Uploaded from ${os} (${clearBrowser})` : `Uploaded from ${os}`
        }
        return undefined
    }
    get pageUrl() { return `/trees/${this.id}` }
}

export class SyncOptions {
    includeLivingPersons = false
    livingStartItemId?: string
    includeContent = false
}

export type OS = "windows" | "mac"

export interface OSInfo {
    id: OS
    name: string
    versions: string
    icon: any
}

export const osInfos: OSInfo[] = [
    { 
        id: "windows", 
        name: 'Windows', 
        versions: "10, 11",
        icon: new URL('@/assets/logos/Windows_150.png', import.meta.url).href,
    },
    { 
        id: "mac", 
        name: 'Mac', 
        versions: "10",
        icon: new URL('@/assets/logos/Mac_150.png', import.meta.url).href,
    },
]

export interface WorkspaceTypeInfo {
    id: string
    description: string
    product: string
    infoUrl: string
    icon: any
    fileExtensions: string[]
    versions: string
    availableOn: OS[]
    supportedOn: OS[]
    supportAlerts: string[]
    plannedOn: OS[]
    hidden?: boolean
}

export const workspaceTypeInfos: WorkspaceTypeInfo[] = [
    {
        id: 'TestData',
        description: 'Test Data file',
        product: "Test Data",
        infoUrl: 'https://www.example.com/',
        icon: new URL('@webapp/assets/logos/TestData_file.png', import.meta.url).href,
        fileExtensions: ['.tdf'],
        versions: "1",
        availableOn: ['windows', 'mac'],
        supportedOn: ['windows', 'mac'],
        supportAlerts: [],
        plannedOn: [],
        hidden: true,
    },
    {
        id: 'RootsMagic', 
        description: 'RootsMagic file',
        product: "RootsMagic",
        infoUrl: 'https://www.rootsmagic.com/',
        icon: new URL('@webapp/assets/logos/RootsMagic_app.png', import.meta.url).href,
        fileExtensions: ['.rmgc', '.rmtree'],
        versions: "8, 9, 10, 11",
        availableOn: ['windows', 'mac'],
        supportedOn: ['windows', 'mac'],
        supportAlerts: [],
        plannedOn: []
    },
    {
        id: 'Legacy', 
        description: 'Legacy Family Tree file', 
        product: "Legacy Family Tree", 
        infoUrl: 'https://www.legacyfamilytree.com/',
        icon: new URL('@webapp/assets/logos/Legacy_app_circle.png', import.meta.url).href,
        fileExtensions: ['.fdb'],
        versions: "8, 9, 10",
        availableOn: ['windows'],
        supportedOn: ['windows'],
        supportAlerts: [],
        plannedOn: [],
    },
    {
        id: 'FamilyTreeMaker', 
        description: 'Family Tree Maker file', 
        product: "Family Tree Maker", 
        infoUrl: 'https://www.mackiev.com/ftm/',
        icon: new URL('@webapp/assets/logos/FamilyTreeMaker_app.png', import.meta.url).href,
        fileExtensions: ['.ftm'],
        versions: "2019, 2024",
        availableOn: ['windows', 'mac'],
        supportedOn: ['windows'],
        supportAlerts: [],
        plannedOn: ['mac'],
    },
    {
        id: 'AncestralQuest', 
        description: 'Ancestral Quest file', 
        product: "Ancestral Quest", 
        infoUrl: 'https://www.ancquest.com',
        icon: new URL('@webapp/assets/logos/AncestralQuest_app.jpg', import.meta.url).href,
        fileExtensions: ['.aq'],
        versions: "16",
        availableOn: ['windows', 'mac'],
        supportedOn: ['windows', 'mac'],
        supportAlerts: [],
        plannedOn: [],
    },
    { 
        id: 'MacFamilyTree', 
        description: 'MacFamilyTree file', 
        product: "MacFamilyTree", 
        infoUrl: 'https://www.syniumsoftware.com/macfamilytree',
        icon: new URL('@webapp/assets/logos/MacFamilyTree_app.png', import.meta.url).href,
        fileExtensions: ['.mftree'],        
        versions: "11",
        availableOn: ['mac'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: [],
    },
    { 
        id: 'FamilyHistorian', 
        description: 'Family Historian file', 
        product: "Family Historian", 
        infoUrl: 'https://www.family-historian.co.uk/',
        icon: new URL('@webapp/assets/logos/FamilyHistorian_app.png', import.meta.url).href,
        fileExtensions: ['.fh_proj'],
        versions: "7",
        availableOn: ['windows'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: ['windows'],
    },
    {
        id: 'Reunion', 
        description: 'Reunion file', 
        product: "Reunion", 
        infoUrl: 'https://www.leisterpro.com/',
        icon: new URL('@webapp/assets/logos/Reunion_app.png', import.meta.url).href,
        fileExtensions: ['.familyFile'],
        versions: "14",
        availableOn: ['mac'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: ['mac'],
    },
    {
        id: 'Gramps', 
        description: 'Gramps file', 
        product: "Gramps", 
        infoUrl: 'https://gramps-project.org',
        icon: new URL('@webapp/assets/logos/Gramps_app.png', import.meta.url).href,
        fileExtensions: ['.db'],
        versions: "6.0",
        availableOn: ['windows', 'mac'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: ['windows', 'mac'],
    },
    {
        id: 'PAF', 
        description: 'Personal Ancestral File', 
        product: "Personal Ancestral File", 
        infoUrl: 'https://www.familysearch.org/en/blog/personal-ancestral-file-paf-is-discontinued',
        icon: new URL('@webapp/assets/logos/PAF_app.png', import.meta.url).href,
        fileExtensions: ['.paf'],
        versions: "5.2",
        availableOn: ['windows'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: [],
    },
    {
        id: 'GEDCOM', 
        description: 'GEDCOM file', 
        product: "GEDCOM",
        infoUrl: 'https://gedcom.io',
        icon: new URL('@webapp/assets/logos/GEDCOM.png', import.meta.url).href,
        fileExtensions: ['.ged'],
        versions: "5.5.1",
        availableOn: ['windows', 'mac'],
        supportedOn: [],
        supportAlerts: [],
        plannedOn: [],
    }
]

export function isWorkspaceTypeSupportedOn(workspaceTypeId: string, os?: OS) {
    const info = workspaceTypeInfos.find(i => i.id == workspaceTypeId)
    return !!os && !!info?.supportedOn.includes(os)
}

export function isWorkspaceTypePlannedOn(workspaceTypeId: string, os?: OS) {
    const info = workspaceTypeInfos.find(i => i.id == workspaceTypeId)
    return !!os && !!info?.plannedOn.includes(os)
}

export function getSupportedFileExtensions(os?: OS, includeHidden = false) {
    if (!os) {
        return []
    }
    return workspaceTypeInfos
        .filter(t => (includeHidden || !t.hidden) && t.supportedOn.includes(os))
        .flatMap(t => t.fileExtensions)
}

export const currentOS: OS | undefined = 
    // exclude ios/android first to avoid false matches
    /iphone|ipad|ipod/i.test(navigator.userAgent) ? undefined :
    /android/i.test(navigator.userAgent) ? undefined :
    /win/i.test(navigator.userAgent) ? 'windows'  : 
    /mac/i.test(navigator.userAgent) ? 'mac' :
    undefined

export enum MapTo
{
    Client,
    Server,
}

export class SyncSource {
    dataFileType?: string
    dataFilePath?: string
    dataFileSize?: number
    dataFileHash?: string
    modifiedDate?: DateTime
}

export interface SyncSourceItemCounts {
    total: number
    inScope: number
}

export class SyncSourceDetails {
    itemCounts: Partial<Record<ItemType, SyncSourceItemCounts>> = {}
}

export class Sync {
    id?: string
    viewGroupId?: string
    workspaceId?: string
    syncSessionId?: string
    source?: SyncSource
    sourceDetails?: SyncSourceDetails
    syncAgent?: string
    syncAgentVersion?: string
    state = SyncState.Active
    completedDate?: DateTime
    createdDate?: DateTime
    createdUserId?: string
    workspace?: Workspace

    get final() { return Sync.isFinalState(this.state) }
    get completed() { return this.state == SyncState.Completed }
    get finalIncomplete() { return this.final && !this.completed }

    static isFinalState(state: SyncState) {
        return state == SyncState.Completed || state == SyncState.Cancelled || state == SyncState.Failed
    }
}

export enum SyncState
{
    Active = "Active",         // open, accepting changes
    Processing = "Processing", // changes staged as one upload, being applied
    Completing = "Completing", // changes applied, post-processing
    Cancelled = "Cancelled",
    Failed = "Failed",
    Completed = "Completed",
}

export class TrackingLink {
    itemType = ItemType.Person
    itemId?: string
    trackedItemId?: string
    createdDate?: DateTime
    createdUserId?: string
}

export interface ScopeStatistics {
    groupId: string
    personCount: number
    familyCount: number
    assertionCount: number
    placeCount: number
    siteCount: number
    contentCount: number
    contentTagCount: number
}

export enum Gender
{
    Male = "Male",
    Female = "Female",
}

export const genderImages: any = {
    [Gender.Male]: new URL("@/assets/icons/male_gray_150.png", import.meta.url).href,
    [Gender.Female]: new URL("@/assets/icons/female_gray_150.png", import.meta.url).href,
    ["default"]: new URL("@/assets/icons/person_gray_150.png", import.meta.url).href,
}

export const dottedPersonImage = new URL("@/assets/icons/person_dotted_80.png", import.meta.url).href

export enum ItemType
{
    Person = "Person",
    Family = "Family",
    Assertion = "Assertion",
    Place = "Place",
    Site = "Site",
    AssertionSubject = "AssertionSubject",
    Content = "Content",
    ContentTag = "ContentTag",
}

export enum AssertionType {
    Birth = "Birth",
    Gender = "Gender",
    Name = "Name",
    Child = "Child",
    Death = "Death",
    Christen = "Christen",
    Burial = "Burial",
    Cremation = "Cremation",
    Adoption = "Adoption",
    Baptism = "Baptism",
    BarMitzvah = "BarMitzvah",
    BasMitzvah = "BasMitzvah",
    Blessing = "Blessing",
    ChristenAdult = "ChristenAdult",
    Confirmation = "Confirmation",
    FirstComm = "FirstComm",
    Ordination = "Ordination",
    Naturalization = "Naturalization",
    Emigration = "Emigration",
    Immigration = "Immigration",
    Census = "Census",
    Probate = "Probate",
    Will = "Will",
    Graduation = "Graduation",
    Retirement = "Retirement",
    Description = "Description",
    Education = "Education",
    Nationality = "Nationality",
    Employment = "Employment",
    Occupation = "Occupation",
    Property = "Property",
    Religion = "Religion",
    Residence = "Residence",
    SSN = "SSN",
    LDSBaptism = "LDSBaptism",
    LDSEndow = "LDSEndow",
    LDSSealPar = "LDSSealPar",
    AFN = "AFN",
    Ref = "Ref",
    Caste = "Caste",
    Title = "Title",
    LDSConf = "LDSConf",
    LDSInit = "LDSInit",
    Degree = "Degree",
    Military = "Military",
    Mission = "Mission",
    Stillborn = "Stillborn",
    Illness = "Illness",
    Living = "Living",
    Elected = "Elected",
    Excomm = "Excomm",
    Namesake = "Namesake",
    AltName = "AltName",
    DNA = "DNA",
    Misc = "Misc",

    Cemetery = "Cemetery",
    Circumcision = "Circumcision",
    Court = "Court",
    Email = "Email",
    Funeral = "Funeral",
    GodFather = "GodFather",
    GodMother = "GodMother",
    Health = "Health",
    Hobbies = "Hobbies",
    Honors = "Honors",
    Hospital = "Hospital",
    Land = "Land",
    Medical = "Medical",
    MilitaryAward = "MilitaryAward",
    MilitaryDischarge = "MilitaryDischarge",
    Obituary = "Obituary",
    Ordinance = "Ordinance",
    Pension = "Pension",
    PensionApp = "PensionApp",
    PhysDesc = "PhysDesc",
    School = "School",
    Travel = "Travel",
    URL = "URL",
    WillFiled = "WillFiled",

    // Family assertion types
    Marriage = "Marriage",
    Annulment = "Annulment",
    Divorce = "Divorce",
    DivorceFiled = "DivorceFiled",
    Engagement = "Engagement",
    MarrBanns = "MarrBanns",
    MarrContract = "MarrContract",
    MarrLicense = "MarrLicense",
    MarrSettlement = "MarrSettlement",
    LDSSealSp = "LDSSealSp",
    ResidenceFam = "ResidenceFam",
    CensusFam = "CensusFam",
    Separation = "Separation",

    MarrNotice = "MarrNotice",
}